import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    SortableHeader,
    TablePagination,
    TableSearch,
} from "./ReportingLab.jsx";

const AbcMode = {
    Turnover: "turnover",
    Amount: "amount",
};

const AbcColors = {
    A: "#16a34a",
    B: "#f97316",
    C: "#64748b",
};

function AbcModeToggle({mode, setMode, t}) {
    return <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        {[
            {id: AbcMode.Turnover, label: t("reporting_lab.abc.by_revenue", {defaultValue: "Podle obratu"})},
            {id: AbcMode.Amount, label: t("reporting_lab.abc.by_quantity", {defaultValue: "Podle kusů"})},
        ].map((item) => <button
            key={item.id}
            type="button"
            onClick={() => setMode(item.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${mode === item.id ? "bg-violet-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"}`}
        >
            {item.label}
        </button>)}
    </div>;
}

function mapAbcProducts(products, mode) {
    return products.map((product) => ({
        product,
        metricValue: mode === AbcMode.Turnover ? moneyToMajor(product.turnover) : Number(product.amount ?? 0),
        turnover: moneyToMajor(product.turnover),
        amount: Number(product.amount ?? 0),
        group: product.abcGroup,
        share: Number(product.abcShare ?? 0),
        cumulativeShare: Number(product.abcCumulativeShare ?? 0),
    }));
}

export default function ReportingAbcLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState(AbcMode.Turnover);
    const [selectedGroup, setSelectedGroup] = useState("all");
    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(25);
    const [tableSearch, setTableSearch] = useState("");
    const [debouncedTableSearch, setDebouncedTableSearch] = useState("");
    const [tableSort, setTableSort] = useState({key: "turnover", direction: "desc"});

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const productAssortmentPerformanceFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
        limit: tablePageSize,
        page: tablePage,
        search: debouncedTableSearch || null,
        group: selectedGroup === "all" ? null : selectedGroup,
        orderBy: tableSort.key,
        orderDirection: tableSort.direction,
        mode: "gross",
        sortBy: mode === AbcMode.Turnover ? "turnover" : "amount",
    }), [sinceString, untilString, centreFilterKey, domainFilterKey, mode, tablePageSize, tablePage, debouncedTableSearch, selectedGroup, tableSort]);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedTableSearch(tableSearch.trim()), 300);
        return () => window.clearTimeout(timeout);
    }, [tableSearch]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setData(null);

        const api = new ErpCalculationApi(getAuthorityConfiguration());

        api.productAssortmentPerformance({
                projectId: project.id,
                productAssortmentPerformanceFilter,
            })
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setData(nextData);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, productAssortmentPerformanceFilter]);

    const products = data?.products ?? [];
    const summary = data?.summary ?? {};
    const topProducts = data?.topProducts ?? [];
    const totalItems = Number(data?.totalItems ?? 0);
    const sourceTotalItems = Number(summary.sourceTotalItems ?? 0);
    const classifiedItems = Number(summary.classifiedItems ?? 0);
    const abcRows = useMemo(() => mapAbcProducts(products, mode), [products, mode]);
    const paretoRows = useMemo(() => {
        const rows = mapAbcProducts(topProducts, mode);
        const displayedTotal = rows.reduce((sum, row) => sum + Math.max(row.metricValue, 0), 0);
        let displayedCumulative = 0;

        return rows.map((row) => {
            displayedCumulative += Math.max(row.metricValue, 0);

            return {
                ...row,
                displayedCumulativeShare: displayedTotal > 0 ? displayedCumulative / displayedTotal * 100 : 0,
            };
        });
    }, [topProducts, mode]);
    const groups = (summary.groups ?? []).map((group) => ({
        ...group,
        value: mode === AbcMode.Turnover ? Number(group.value ?? 0) / 100 : Number(group.value ?? 0),
        average: Number(group.count ?? 0) > 0
            ? (mode === AbcMode.Turnover ? Number(group.value ?? 0) / 100 : Number(group.value ?? 0)) / Number(group.count)
            : 0,
    }));
    const currency = [...topProducts, ...products].find((product) => product.turnover?.currency)?.turnover?.currency ?? projectCurrency(project);
    const topProduct = paretoRows[0];
    const safeTablePage = Math.min(tablePage, Math.max(1, Number(data?.totalPages ?? 1)));
    const visibleAbcRows = abcRows;
    const groupA = groups.find((group) => group.group === "A");
    const groupB = groups.find((group) => group.group === "B");
    const groupC = groups.find((group) => group.group === "C");
    const groupCounts = {
        A: groupA?.count ?? 0,
        B: groupB?.count ?? 0,
        C: groupC?.count ?? 0,
    };
    const toggleSort = (key) => {
        setTableSort((current) => current.key === key
            ? {key, direction: current.direction === "asc" ? "desc" : "asc"}
            : {key, direction: "desc"});
        setTablePage(1);
    };
    const tableActions = <div className="flex flex-wrap justify-end gap-2">
        <button
            type="button"
            onClick={() => { setSelectedGroup("all"); setTablePage(1); }}
            className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-semibold ${selectedGroup === "all" ? "border-violet-500 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}
        >
            {t("reporting_lab.abc.all", {defaultValue: "Vše"})} ({renderNumber(classifiedItems)})
        </button>
        {["A", "B", "C"].map((group) => <button
            key={group}
            type="button"
            onClick={() => { setSelectedGroup(group); setTablePage(1); }}
            className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-semibold ${selectedGroup === group ? "border-violet-500 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}
        >
            {group} ({renderNumber(groupCounts[group])})
        </button>)}
    </div>;
    const valueFormatter = mode === AbcMode.Turnover
        ? (value) => formatChartCurrency(value, currency)
        : formatChartNumber;
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const groupShareOption = {
        color: [AbcColors.A, AbcColors.B, AbcColors.C],
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => `${params.marker} ${params.name}<span style="float:right;margin-left:16px;font-weight:600">${valueFormatter(params.value)}</span><br/><span style="color:#64748b">${formatChartPercent(params.percent)}</span>`,
        },
        legend: {bottom: 0, ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["42%", "64%"],
                center: ["50%", "42%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartPercent(params.percent)}`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: groups.map((group) => ({
                    name: `${t("reporting_lab.abc.group", {defaultValue: "Skupina"})} ${group.group}`,
                    value: group.value,
                })),
            },
        ],
    };

    const paretoOption = {
        color: ["#2563eb", "#7c3aed"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 50, bottom: 64, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, [
                valueFormatter,
                (value) => formatChartPercent(value, 1),
            ]),
        },
        legend: {top: 4, right: 18, ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: paretoRows.map((row) => row.product.productName),
            axisLabel: {fontSize: 12, interval: 0, rotate: 45, width: 120, overflow: "truncate", margin: 12, align: "right"},
        },
        yAxis: [
            {
                type: "value",
                axisLabel: {fontSize: 12, formatter: valueFormatter},
            },
            {
                type: "value",
                min: 0,
                max: 100,
                axisLabel: {fontSize: 12, formatter: (value) => formatChartPercent(value)},
            },
        ],
        series: [
            {
                name: mode === AbcMode.Turnover ? t("reporting_lab.series.revenue", {defaultValue: "Obrat"}) : t("reporting_lab.products.quantity", {defaultValue: "Kusy"}),
                type: "bar",
                data: paretoRows.map((row) => ({
                    value: row.metricValue,
                    itemStyle: {color: AbcColors[row.group]},
                })),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.abc.cumulative_share", {defaultValue: "Kumulativní podíl"}),
                type: "line",
                yAxisIndex: 1,
                smooth: true,
                data: paretoRows.map((row) => row.displayedCumulativeShare),
            },
        ],
    };

    const groupImpactOption = {
        color: ["#2563eb", "#7c3aed"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 28, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => {
                const items = Array.isArray(params) ? params : [params];
                const group = groups[items[0]?.dataIndex] ?? {};
                const title = `${t("reporting_lab.abc.group", {defaultValue: "Skupina"})} ${group.group}`;

                return [
                    title,
                    `${items[0]?.marker ?? ""} ${items[0]?.seriesName ?? ""}<span style="float:right;margin-left:16px;font-weight:600">${valueFormatter(group.value ?? 0)}</span>`,
                    `${items[1]?.marker ?? ""} ${items[1]?.seriesName ?? ""}<span style="float:right;margin-left:16px;font-weight:600">${formatChartNumber(group.count ?? 0)}</span>`,
                    `<span style="color:#64748b">${t("reporting_lab.abc.group_share", {defaultValue: "Podíl ABC skupin"})}: ${formatChartPercent(group.share ?? 0, 1)}</span>`,
                    `<span style="color:#64748b">${t("reporting_lab.abc.average_per_product", {defaultValue: "Průměr na produkt"})}: ${valueFormatter(group.average ?? 0)}</span>`,
                ].join("<br/>");
            },
        },
        legend: {bottom: 0, ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: groups.map((group) => `${t("reporting_lab.abc.group", {defaultValue: "Skupina"})} ${group.group}`),
            axisLabel: {fontSize: 12},
        },
        yAxis: [
            {
                type: "value",
                axisLabel: {fontSize: 12, formatter: valueFormatter},
            },
            {
                type: "value",
                axisLabel: {fontSize: 12, formatter: formatChartNumber},
            },
        ],
        series: [
            {
                name: mode === AbcMode.Turnover ? t("reporting_lab.series.revenue", {defaultValue: "Obrat"}) : t("reporting_lab.products.quantity", {defaultValue: "Kusy"}),
                type: "bar",
                data: groups.map((group) => ({
                    value: group.value,
                    itemStyle: {color: AbcColors[group.group]},
                })),
                barMaxWidth: 54,
            },
            {
                name: t("reporting_lab.abc.product_count", {defaultValue: "Počet produktů"}),
                type: "line",
                yAxisIndex: 1,
                smooth: true,
                data: groups.map((group) => group.count),
            },
        ],
    };

    return <WideSecureLayout
        title={t("reporting_lab.pages.abc", {defaultValue: "ABC analýza"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
    >
        <div className="grid grid-cols-12 gap-4">
            <ReportingPageDescription
                className="col-span-12"
                title={t("reporting_lab.abc.explanation_title", {defaultValue: "K čemu slouží ABC analýza"})}
                actions={<AbcModeToggle mode={mode} setMode={(nextMode) => {
                    setMode(nextMode);
                    setTableSort({key: nextMode === AbcMode.Turnover ? "turnover" : "amount", direction: "desc"});
                    setTablePage(1);
                }} t={t}/>}
            >
                {t("reporting_lab.abc.explanation", {defaultValue: "ABC analýza rozdělí produkty podle významu pro obrat nebo prodané kusy. Skupina A táhne hlavní výkon a zaslouží největší pozornost, skupina B je stabilní střed a skupina C ukazuje dlouhý chvost produktů s nízkým dopadem."})}
            </ReportingPageDescription>

            <div className="col-span-12 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                <KpiCard loading={loading} label={t("reporting_lab.abc.products", {defaultValue: "Produkty v analýze"})} value={renderNumber(classifiedItems)} meta={t("reporting_lab.abc.full_scope", {defaultValue: "Celý sortiment dle aktuálních filtrů"})}/>
                <KpiCard loading={loading} label={t("reporting_lab.abc.group_a", {defaultValue: "Skupina A"})} value={renderNumber(groupA?.count ?? 0)} meta={<span className="inline-flex rounded-md bg-emerald-100 px-2 py-1 font-semibold text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-200">{renderPercentValue(groupA?.share ?? 0)}</span>} tone="good"/>
                <KpiCard loading={loading} label={t("reporting_lab.abc.group_b", {defaultValue: "Skupina B"})} value={renderNumber(groupB?.count ?? 0)} meta={<span className="inline-flex rounded-md bg-amber-100 px-2 py-1 font-semibold text-amber-700 dark:bg-amber-950/60 dark:text-amber-200">{renderPercentValue(groupB?.share ?? 0)}</span>} tone="warn"/>
                <KpiCard loading={loading} label={t("reporting_lab.abc.group_c", {defaultValue: "Skupina C"})} value={renderNumber(groupC?.count ?? 0)} meta={<span className="inline-flex rounded-md bg-slate-100 px-2 py-1 font-semibold text-slate-700 dark:bg-slate-900/60 dark:text-slate-200">{renderPercentValue(groupC?.share ?? 0)}</span>}/>
                <KpiCard
                    loading={loading}
                    label={t("reporting_lab.abc.top_product_share", {defaultValue: "Podíl top produktu"})}
                    value={renderPercentValue(topProduct?.share ?? 0)}
                    meta={<span className="block truncate" title={topProduct?.product.productName ?? undefined}>{topProduct?.product.productName ?? "-"}</span>}
                    emphasizeMeta={false}
                />
            </div>

            <Panel loading={loading} title={t("reporting_lab.abc.pareto", {defaultValue: "Pareto křivka top 20 produktů"})} className="col-span-12 xl:col-span-8">
                <EChart option={paretoOption} className="h-[28rem]"/>
            </Panel>
            <Panel loading={loading} title={t("reporting_lab.abc.group_share", {defaultValue: "Podíl ABC skupin"})} className="col-span-12 xl:col-span-4">
                <EChart option={groupShareOption} className="h-[28rem]"/>
            </Panel>

            <Panel loading={loading} title={t("reporting_lab.abc.group_impact", {defaultValue: "Význam skupin ABC"})} className="col-span-12 xl:col-span-5">
                <EChart option={groupImpactOption} className="h-96"/>
            </Panel>

            <Panel
                loading={loading}
                title={t("reporting_lab.abc.table", {defaultValue: "ABC klasifikace produktů"})}
                actions={tableActions}
                className="col-span-12 xl:col-span-7"
            >
                <TableSearch value={tableSearch} onChange={(value) => { setTableSearch(value); setTablePage(1); }} visible={classifiedItems > 15} t={t}/>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                        <thead>
                        <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                            <SortableHeader label={t("reporting_lab.products.product", {defaultValue: "Produkt"})} sortKey="product" sort={tableSort} onSort={toggleSort}/>
                            <SortableHeader label="ABC" sortKey="group" sort={tableSort} onSort={toggleSort}/>
                            <SortableHeader label={t("reporting_lab.abc.share", {defaultValue: "Podíl"})} sortKey="share" sort={tableSort} onSort={toggleSort} align="right"/>
                            <SortableHeader label={t("reporting_lab.abc.cumulative_share", {defaultValue: "Kumulativní podíl"})} sortKey="cumulativeShare" sort={tableSort} onSort={toggleSort} align="right"/>
                            <SortableHeader label={t("reporting_lab.products.quantity", {defaultValue: "Kusy"})} sortKey="amount" sort={tableSort} onSort={toggleSort} align="right"/>
                            <SortableHeader label={t("reporting_lab.table.revenue", {defaultValue: "Obrat"})} sortKey="turnover" sort={tableSort} onSort={toggleSort} align="right"/>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {visibleAbcRows.map((row) => <tr key={`${row.product.integrationId ?? "integration"}-${row.product.productRemoteId ?? row.product.productName}`}>
                            <td className="max-w-[260px] py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">
                                <div className="truncate">{row.product.productName}</div>
                            </td>
                            <td className="py-3 pr-4 text-center">
                                <span className="inline-flex min-w-8 justify-center rounded-full px-2 py-1 text-xs font-bold text-white" style={{backgroundColor: AbcColors[row.group]}}>{row.group}</span>
                            </td>
                            <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.share)}</td>
                            <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.cumulativeShare)}</td>
                            <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.amount)}</td>
                            <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue({amount: Math.round(row.turnover * 100), currency}, true)}</td>
                        </tr>)}
                        </tbody>
                    </table>
                </div>
                <TablePagination page={safeTablePage} pageSize={tablePageSize} totalItems={totalItems} onPageChange={setTablePage} onPageSizeChange={(value) => { setTablePageSize(value); setTablePage(1); }} pageSizeOptions={[25, 50, 100]} t={t}/>
                {totalItems === 0 && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.abc.empty", {defaultValue: "Pro vybrané období nejsou dostupná produktová data pro ABC analýzu."})}</div>}
                {classifiedItems > 0 && <div className="mt-4 text-xs text-slate-500 dark:text-slate-400">{t("reporting_lab.abc.scope_note", {defaultValue: "Výpočet respektuje aktuální období, domény a střediska a běží nad celým sortimentem dostupným pro zvolené filtry."})} {t("reporting_lab.abc.source_count", {defaultValue: "Zdroj obsahuje {{count}} produktů.", count: renderNumber(sourceTotalItems)})}</div>}
            </Panel>
        </div>
    </WideSecureLayout>;
}
